import { fetchAvailability, setBookingRequirement, setOffer, setOfferNotification, setPassengers } from 'component/page/detail/store/availabilityIndex';
import { DispatchProps, OwnProps, Props, StateProps } from 'component/page/detail/PackageOptions/CollapsedOffer/OfferCardBottomSection/OfferCardFooter.types';
import {
    selectBehaviorFromBootstrap,
    selectCmsFromBootstrap,
    selectConfigFromBootstrap,
    selectExperimentsFromBootstrap,
    selectFeatureBehaviorFromBootstrap,
} from 'store/slice/bootstrap';
import {
    selectActivityDetail,
    selectAvailabilityIsLoading,
    selectBasedCalendarOfferIds,
    selectOfferLevelSelectedDate,
    selectPassengerOption,
} from 'component/page/detail/store/availabilitySelectorIndex';
import { createSelector, Dispatch } from '@reduxjs/toolkit';
import { OfferNotificationType, SelectedOfferState } from 'component/page/detail/store/availabilityIndex.types';
import { scrollToTargetAdjusted } from 'core-framework/util/Dom';
import { selectActivityTokenFromDetailState, selectIsExpandFirstOfferCardInitiated } from 'component/page/detail/store/detail.selectors';
import { CARD_EXPAND_TIME_OFFSET, TAB_HEADER_OFFSET } from 'component/common/DetailInViewWrapper/navigateToHashWithTabBarOffset';
import { selectScreenSize } from 'store/slice/screen';
import { Pax } from 'gql/autogen/Pax';
import { ActivityOfferToken } from 'core/type/Details.types';
import { selectOfferLevelCalendarsFromCalendarState, selectSchedulesFromCalendarState } from 'component/page/detail/store/calendar.selectors';
import { EnrichedOfferOption } from 'gql/autogen/EnrichedOfferOption';
import { getCheapestSchedules, mapPriceNumbers } from 'component/page/detail/PopoverCalendar/PopoverCalendarUtils';
import { getDateInYmdFormat } from 'core-framework/util/datetime/date';
import { setIsExpandFirstOfferCardInitiated } from 'component/page/detail/store/details';
import { showNotification } from 'store/slice/notification';
import { isB } from 'core/window/bootstrapConfig';
import { ExperimentId } from 'core/type/ExperimentId';

export const mapStateToProps = createSelector(
    selectConfigFromBootstrap,
    selectCmsFromBootstrap,
    selectActivityDetail,
    selectPassengerOption,
    selectOfferLevelSelectedDate,
    selectBehaviorFromBootstrap,
    selectAvailabilityIsLoading,
    selectActivityTokenFromDetailState,
    selectExperimentsFromBootstrap,
    selectScreenSize,
    selectOfferLevelCalendarsFromCalendarState,
    selectSchedulesFromCalendarState,
    selectBasedCalendarOfferIds,
    selectIsExpandFirstOfferCardInitiated,
    selectFeatureBehaviorFromBootstrap,
    (
        config,
        cms,
        detail,
        passengers: Pax[],
        selectedDate,
        behavior,
        isAvailLoading,
        activityTokenFromState,
        experiments,
        screenSize,
        offerLevelCalendars,
        activityLevelSchedules,
        basedCalendarOfferIds,
        isExpandFirstOfferCardInitiated,
        featureBehaviours,
    ): StateProps => {
        const priceBehavior = behavior.common.price;
        const showInclusiveTax = behavior.common.showInclusiveTax;
        const maintainSelectedOfferOnDetails = featureBehaviours.MaintainSelectedOfferOnDetails;
        const expandFirstOfferCardOnDetailsLanding = featureBehaviours.ExpandFirstOfferOnDetailsLanding;
        const continueYourBooking = featureBehaviours.ContinueYourBooking;
        const isFixedWrongOfferOption = isB(ExperimentId.ACT_5289, config.experiments);
        return {
            cms,
            detail,
            passengers,
            selectedDate,
            priceBehavior,
            isAvailLoading,
            activityTokenFromState,
            screenSize,
            showInclusiveTax,
            offerLevelCalendars,
            activityLevelSchedules,
            basedCalendarOfferIds,
            expandFirstOfferCardOnDetailsLanding,
            isExpandFirstOfferCardInitiated,
            experiments,
            continueYourBooking,
            maintainSelectedOfferOnDetails,
            isFixedWrongOfferOption,
        };
    },
);

export const mapDispatchToProps = (dispatch: Dispatch): DispatchProps => ({
    onSelectOffer: (offerToken: ActivityOfferToken, navigationHash: string, navigationEnabled: boolean) => {
        dispatch(setOfferNotification({ [OfferNotificationType.DateChip]: false }));
        fetchAvailability(offerToken.activityToken, { excludePassengers: false, enableOfferFilter: offerToken.enableOfferFilter });
        if (navigationEnabled) {
            setTimeout(() => {
                scrollToTargetAdjusted(navigationHash, TAB_HEADER_OFFSET);
            }, CARD_EXPAND_TIME_OFFSET);
        }
    },
    setBookingRequirementById: (bookingRequirementId: number) => {
        dispatch(setBookingRequirement({ bookingRequirementId, isResetSearchCriteriaStatus: true }));
    },
    setIsExpandFirstOfferCardInitiated: (expandFirstCardOnLanding: boolean) => {
        dispatch(setIsExpandFirstOfferCardInitiated({ expandFirstCardOnLanding }));
    },
    setOffer: (offer: SelectedOfferState) => {
        dispatch(setOffer(offer));
    },
    setPassengers: (passengers: Pax[]) => {
        dispatch(setPassengers(passengers));
    },
    setShowContinueYourBookingNotification: (notificationText: string) => {
        dispatch(showNotification(notificationText));
    },
});

export const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: OwnProps): Props => {
    const { selectedDate, offerLevelCalendars, activityLevelSchedules, basedCalendarOfferIds } = stateProps;

    return {
        ...stateProps,
        ...dispatchProps,
        ...ownProps,
        getCalendarPrice: (possibleOffers: EnrichedOfferOption[]) => {
            const cheapestSchedules = getCheapestSchedules(possibleOffers, activityLevelSchedules, offerLevelCalendars, basedCalendarOfferIds);
            const offerLevelPriceSchedules = mapPriceNumbers(cheapestSchedules);
            const calendarPrice =
                offerLevelPriceSchedules.length > 0
                    ? offerLevelPriceSchedules.find((label) => getDateInYmdFormat(label.date) === getDateInYmdFormat(selectedDate))?.price || offerLevelPriceSchedules[0].price
                    : 0;
            return calendarPrice;
        },
    };
};
